import {
  HTTP_STATUS,
  isLegacySuccessCode,
  isSuccessCode,
  kindOf,
  mapLegacyCode,
} from './contract';
import { fallbackMessage } from './messages';

/**
 * Reports whether a value is a plain object rather than an array or null.
 *
 * @param {*} value - Value to test.
 * @returns {boolean} True for plain objects.
 */
const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Detects the legacy response format.
 *
 * A legacy envelope carries `message_id` or `message_en`, while the current
 * contract carries a single `message` key.
 *
 * @param {*} body - Raw response body.
 * @returns {boolean} True when the body uses the legacy format.
 */
const isLegacyEnvelope = (body) =>
  isPlainObject(body) && ('message_id' in body || 'message_en' in body);

/**
 * Guesses whether an object holds per-field errors.
 *
 * This heuristic runs only on the legacy path, where the `data` key doubles as
 * both a success payload and an error container. At least one value must be an
 * array, which keeps `{ message: 'KTP already exist' }` from being mistaken for
 * a field error map.
 *
 * @param {*} value - Candidate error container.
 * @returns {boolean} True when the object looks like a field error map.
 */
const looksLikeFieldErrors = (value) =>
  isPlainObject(value) &&
  Object.values(value).some(Array.isArray) &&
  Object.values(value).every(
    (item) => Array.isArray(item) || typeof item === 'string'
  );

/**
 * Flattens any error value into a list of strings.
 *
 * @param {*} value - Raw error value for one field.
 * @returns {string[]} Normalized messages.
 */
const toMessageList = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item != null).map(String);
  }
  if (value == null) return [];
  if (isPlainObject(value)) return Object.values(value).flatMap(toMessageList);
  return [String(value)];
};

/**
 * Normalizes the `errors` key.
 *
 * The contract defines `errors` as `Object<string, string[]>` or null, but the
 * 500 example in the standardization document sends it as a string. A string is
 * treated as a technical detail and is never rendered underneath a form field.
 *
 * @param {*} raw - Raw `errors` value from the response body.
 * @returns {{ errors: Object<string, string[]>|null, detail: string|null }}
 *   Field errors plus any technical detail found instead of them.
 */
const normalizeErrorBag = (raw) => {
  if (raw == null) return { errors: null, detail: null };

  if (typeof raw === 'string') {
    return { errors: null, detail: raw };
  }

  if (!isPlainObject(raw)) {
    return { errors: null, detail: String(raw) };
  }

  const errors = {};
  Object.keys(raw).forEach((field) => {
    const list = toMessageList(raw[field]);
    if (list.length > 0) errors[field] = list;
  });

  return {
    errors: Object.keys(errors).length > 0 ? errors : null,
    detail: null,
  };
};

/**
 * Picks the message of a legacy envelope according to the active language.
 *
 * @param {Object} body - Legacy response body.
 * @param {string} language - Either `'id'` or `'en'`.
 * @returns {string|null} The message, or null when none is usable.
 */
const pickLegacyMessage = (body, language) => {
  const preferred = language === 'en' ? body.message_en : body.message_id;
  const alternate = language === 'en' ? body.message_id : body.message_en;
  const message = preferred || alternate || body.message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
};

/**
 * Converts a legacy envelope into the current contract shape.
 *
 * In the legacy format the `data` key has two meanings: it holds the payload on
 * success, and on failure it holds the error message in one of three shapes,
 * namely a plain string, an object with a `message` key, or a map of per-field
 * error arrays.
 *
 * @param {Object} body - Legacy response body.
 * @param {string} language - Either `'id'` or `'en'`.
 * @param {number} httpStatus - Actual HTTP status, used when the body code is
 *   one of the generic legacy failure codes.
 * @returns {Object} Normalized envelope without the response-level fields.
 */
const normalizeLegacy = (body, language, httpStatus) => {
  const success = isLegacySuccessCode(body.code);
  const payload = body.data;

  let errors = null;
  let detail = null;
  let data = null;
  let message = pickLegacyMessage(body, language);

  if (success) {
    data = payload ?? null;
  } else if (looksLikeFieldErrors(payload)) {
    ({ errors, detail } = normalizeErrorBag(payload));
  } else if (typeof payload === 'string' && payload.trim() !== '') {
    message = payload;
  } else if (isPlainObject(payload) && typeof payload.message === 'string') {
    message = payload.message;
  } else if (payload != null) {
    data = payload;
  }

  const code = success
    ? HTTP_STATUS.OK
    : mapLegacyCode(body.code, errors !== null, httpStatus);

  return {
    code,
    message,
    errors,
    detail,
    data,
    legacy: true,
    legacyCode: body.code ?? null,
  };
};

/**
 * Reads an envelope that already follows the current contract.
 *
 * @param {Object} body - Response body.
 * @param {number} httpStatus - Actual HTTP status, used when `code` is absent.
 * @returns {Object} Normalized envelope without the response-level fields.
 */
const normalizeCurrent = (body, httpStatus) => {
  const { errors, detail } = normalizeErrorBag(body.errors);
  const code = Number(body.code ?? httpStatus);

  return {
    code: Number.isFinite(code) ? code : httpStatus,
    message:
      typeof body.message === 'string' && body.message.trim() !== ''
        ? body.message
        : null,
    errors,
    detail,
    data: body.data ?? null,
    legacy: false,
    legacyCode: null,
  };
};

/**
 * Supplies a message when the server sent none.
 *
 * The contract makes the server the source of every message, so this is only
 * reached when no message exists to begin with: a 204 response, which RFC 9110
 * forbids from carrying a body, or a response that never reached the
 * application, such as a gateway error page from the reverse proxy.
 *
 * The failure classification doubles as the message key, so codes the contract
 * does not list still resolve sensibly. A 502 or 504 from the proxy reports a
 * server problem rather than an unknown one.
 *
 * @param {number} code - Contract code of the envelope.
 * @param {string} language - Either `'id'` or `'en'`.
 * @returns {string} A message, empty for a successful 200.
 */
const fallbackMessageFor = (code, language) => {
  if (code === HTTP_STATUS.EMPTY) return fallbackMessage(language, 'empty');
  if (isSuccessCode(code)) return '';

  return fallbackMessage(language, kindOf(code));
};

/**
 * Turns a raw transport response into one envelope shape that is identical for
 * every endpoint, whether it follows the current contract or the legacy format.
 *
 * An empty body is expected on 204 responses, where RFC 9110 forbids content,
 * and on non-JSON bodies such as an error page produced by a reverse proxy.
 *
 * @param {Object} params - Response pieces to normalize.
 * @param {*} params.body - The parsed response body.
 * @param {number} params.httpStatus - Actual HTTP status of the response.
 * @param {string|null} [params.requestId] - Correlation ID from the headers.
 * @param {string} [params.language] - Either `'id'` or `'en'`.
 * @returns {{
 *   code: number,
 *   message: string,
 *   errors: Object<string, string[]>|null,
 *   detail: string|null,
 *   data: *,
 *   isEmpty: boolean,
 *   requestId: string|null,
 *   legacy: boolean,
 *   legacyCode: *,
 *   httpStatus: number
 * }} The normalized envelope.
 */
export const normalizeEnvelope = ({
  body,
  httpStatus,
  requestId = null,
  language = 'id',
}) => {
  const hasBody = isPlainObject(body) && Object.keys(body).length > 0;

  let envelope;
  if (Array.isArray(body)) {
    envelope = {
      code: httpStatus,
      message: null,
      errors: null,
      detail: null,
      data: body,
      legacy: false,
      legacyCode: null,
    };
  } else if (!hasBody) {
    envelope = {
      code: httpStatus,
      message: null,
      errors: null,
      detail: typeof body === 'string' && body.trim() !== '' ? body : null,
      data: null,
      legacy: false,
      legacyCode: null,
    };
  } else if (isLegacyEnvelope(body)) {
    envelope = normalizeLegacy(body, language, httpStatus);
  } else {
    envelope = normalizeCurrent(body, httpStatus);
  }

  const isEmpty =
    envelope.code === HTTP_STATUS.EMPTY || httpStatus === HTTP_STATUS.EMPTY;

  return {
    ...envelope,
    isEmpty,
    requestId,
    httpStatus,
    message: envelope.message || fallbackMessageFor(envelope.code, language),
  };
};

export default normalizeEnvelope;
